import { ApiRequestError, api } from "@/lib/api/client";
import type { AuthResult, User } from "@/lib/api/types";
import { clearSession, getSession, setSession } from "@/lib/session/tokens";
import { toLatinDigits } from "@/lib/text/digits";

function badCredentials(error: unknown): boolean {
  return (
    error instanceof ApiRequestError &&
    (error.status === 401 || error.code === "bad_credentials" || error.code === "unauthorized")
  );
}

export async function register(input: {
  email: string;
  password: string;
  displayName: string;
}): Promise<AuthResult> {
  const result = await api<AuthResult>("auth/register", {
    method: "POST",
    body: { ...input, password: toLatinDigits(input.password) },
    anonymous: true,
  });
  setSession(result, result.user);
  return result;
}

export async function login(input: { email: string; password: string }): Promise<AuthResult> {
  const folded = toLatinDigits(input.password);
  const attempt = (password: string) =>
    api<AuthResult>("auth/login", {
      method: "POST",
      body: { ...input, password },
      anonymous: true,
    });

  let result: AuthResult;
  try {
    result = await attempt(folded);
  } catch (error) {
    if (folded === input.password || !badCredentials(error)) throw error;
    result = await attempt(input.password);
  }
  setSession(result, result.user);
  return result;
}

export async function logout(): Promise<void> {
  const session = getSession();
  clearSession();
  if (session === null) return;
  try {
    await api("auth/logout", {
      method: "POST",
      body: { refreshToken: session.refreshToken },
      anonymous: true,
    });
  } catch (error) {
    console.error("logout notify failed:", error);
  }
}

export function me(): Promise<User> {
  return api<User>("auth/me");
}

export function updateProfile(input: {
  displayName?: string;
  avatarUrl?: string | null;
}): Promise<User> {
  return api<User>("auth/me", { method: "PATCH", body: input });
}

export async function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  const newPassword = toLatinDigits(input.newPassword);
  const folded = toLatinDigits(input.currentPassword);
  const attempt = (currentPassword: string) =>
    api<void>("auth/password", { method: "POST", body: { currentPassword, newPassword } });

  try {
    await attempt(folded);
  } catch (error) {
    if (folded === input.currentPassword || !badCredentials(error)) throw error;
    await attempt(input.currentPassword);
  }
}
